import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { API, useAuth } from "@/lib/auth";
import { StatusChip } from "@/components/site/CaseStatus";
import { FolderOpen, ArrowRight, LogOut } from "lucide-react";
import { Button } from "@/components/ui/button";

export default function Portal() {
    const { user, logout } = useAuth();
    const [cases, setCases] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        (async () => {
            try {
                const { data } = await axios.get(`${API}/cases`, {
                    withCredentials: true,
                });
                setCases(data.items || []);
            } finally {
                setLoading(false);
            }
        })();
    }, []);

    return (
        <section data-testid="page-portal" className="py-16 min-h-[70vh]">
            <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-6">
                    <div>
                        <p className="overline">Klientportal</p>
                        <h1 className="mt-3 font-serif font-light text-4xl sm:text-5xl tracking-tight">
                            Välkommen,{" "}
                            <span className="italic text-[#C4A977]">
                                {user?.name?.split(" ")[0] || "klient"}
                            </span>
                        </h1>
                        <p className="mt-3 text-[#A1A1AA]">
                            Här ser du alla dina ärenden och senaste
                            uppdateringar.
                        </p>
                    </div>
                    <Button
                        data-testid="portal-logout"
                        onClick={logout}
                        variant="outline"
                        className="border-white/10 hover:border-[#C4A977] hover:text-[#C4A977] rounded-sm"
                    >
                        <LogOut size={14} className="mr-2" /> Logga ut
                    </Button>
                </div>

                <div className="mt-12">
                    {loading ? (
                        <div className="py-16 text-center text-[#A1A1AA]">
                            Läser in...
                        </div>
                    ) : cases.length === 0 ? (
                        <div
                            data-testid="portal-empty"
                            className="bg-[#111113] border border-white/5 p-12 rounded-sm text-center"
                        >
                            <FolderOpen
                                size={36}
                                className="text-[#C4A977] mx-auto"
                            />
                            <h3 className="mt-6 font-serif text-2xl">
                                Inga ärenden ännu
                            </h3>
                            <p className="mt-3 text-[#A1A1AA] max-w-md mx-auto">
                                Din jurist kopplar ditt ärende till ditt konto
                                så fort det är registrerat. Kontakta oss om du
                                har frågor.
                            </p>
                            <Link
                                to="/kontakt"
                                className="mt-6 inline-flex items-center gap-2 text-[#C4A977] text-sm"
                            >
                                Till kontakt
                                <ArrowRight size={14} />
                            </Link>
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                            {cases.map((c) => (
                                <Link
                                    key={c.id}
                                    to={`/portal/arende/${c.id}`}
                                    data-testid={`case-card-${c.id}`}
                                    className="group block bg-[#111113] border border-white/5 hover:border-[#C4A977]/40 p-7 rounded-sm transition-colors duration-500"
                                >
                                    <div className="flex items-start justify-between gap-4">
                                        <div>
                                            <p className="overline !text-[0.65rem]">
                                                {c.area}
                                            </p>
                                            <h3 className="mt-2 font-serif text-2xl text-[#F4F4F5] group-hover:text-[#C4A977] transition-colors">
                                                {c.title}
                                            </h3>
                                        </div>
                                        <StatusChip status={c.status} />
                                    </div>
                                    <p className="mt-4 text-xs text-[#A1A1AA]">
                                        Senast uppdaterad{" "}
                                        {new Date(
                                            c.updated_at || c.created_at,
                                        ).toLocaleDateString("sv-SE")}
                                    </p>
                                </Link>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </section>
    );
}
